import React, { useState, useEffect, ChangeEvent, useRef } from "react";

import { ITextInputProps, ISearchableMultiSelectProps, IBooleanOptionsProps, ISelectDropdownProps } from "../../interfaces/table_inputs";
import { ISelectDropdownOption } from "../../interfaces/form_elements";

import OutsideAlerter from "../wrappers/OutsideAlerter";
import { SVGIcon } from "../wrappers/SVGIcon";

import { getElipsisString, getPhoneNumberString } from "../../helpers/utility";

import { Icons } from "../../data/Assets";

export const TextInput = (props: ITextInputProps) => {
    const { name, value, onChange, onBlur, placeholder, read_only, pattern_mask, max_length } = props;

    const onChangeInput = (e: ChangeEvent<HTMLInputElement>) => {
        if (read_only) return;

        let value = e.target.value;

        if (pattern_mask === 'integer') {
            value = value.replace(/\D/g, '');
        } else if (pattern_mask === "phone_number") {
            value = getPhoneNumberString(value);
        }

        if (max_length) {
            value = value.slice(0, max_length);
        };

        onChange(value);
    };

    const onBlurInput = () => {
        if (read_only) return;
        if (onBlur) onBlur();
    };

    return (
        <div className="immersion_admin__table_input_wrapper">
            <input
                type="text"
                name={name}
                value={value}
                placeholder={placeholder}
                onChange={onChangeInput}
                onBlur={onBlurInput}
                readOnly={read_only}
            />
        </div>
    );
};

export const SearchableMultiSelect = (props: ISearchableMultiSelectProps) => {
    const { name, values, setValues, placeholder, options, read_only } = props;

    const dropdownContRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);

    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [filteredOptions, setFilteredOptions] = useState<ISelectDropdownOption[]>([]);

    useEffect(() => {
        setSearch('');

        // eslint-disable-next-line
    }, [values]);

    useEffect(() => {
        setSearch('');
        setFilteredOptions(options);

        // eslint-disable-next-line
    }, [options]);

    const onChangeSearch = (e: ChangeEvent<HTMLInputElement>) => {
        if (read_only) return;

        setOpen(true);

        const val = e.target.value;
        setSearch(val);

        const filteredOptions = options.filter(option => option.label.toLowerCase().includes(val.toLowerCase()));
        setFilteredOptions(filteredOptions);
    };

    const onChangeSelect = (option: ISelectDropdownOption) => {
        if (read_only) return;

        const newValues = [...values];
        if (!newValues.includes(option.value)) {
            newValues.push(option.value);

            setOpen(false);
            setSearch('');
            setValues(newValues);
        };
    };

    const onBlurInput = () => {
        if (read_only) return;

        const option = options.find(option => option.label.toLowerCase() === search.toLowerCase());
        if (option) {
            onChangeSelect(option);
        } else {
            setSearch('');
        };
    };

    const onRemoveValue = (value: string) => {
        if (read_only) return;

        const newValues = values.filter(v => v !== value);
        setValues(newValues);
        setSearch('');
        setOpen(false);
    };

    useEffect(() => {
        if (contentRef.current && dropdownContRef.current) {
            contentRef.current.style.minWidth = `${dropdownContRef.current.offsetWidth}px`;
            contentRef.current.style.maxWidth = `${dropdownContRef.current.offsetWidth}px`;

            const rect = dropdownContRef.current.getBoundingClientRect();
            const left = rect.left;
            contentRef.current.style.left = `${left}px`;
        };

        // eslint-disable-next-line
    }, [open]);

    return (
        <div className="immersion_admin__table_input_wrapper">
            <OutsideAlerter callback={() => setOpen(false)} isActive={open}>
                <div className="immersion_admin__ti__searchable_multi_select" ref={dropdownContRef}>
                    <div className="immersion_admin__ti__smls__top">
                        {values.map((value, index) => {
                            return (
                                <div key={index + value} className="immersion_admin__ti__smls__selected_item">
                                    <span>{value}</span>
                                    <button onClick={() => onRemoveValue(value)}>
                                        <SVGIcon src={Icons.X} />
                                    </button>
                                </div>
                            );
                        })}
                        <input
                            type="text"
                            name={name}
                            placeholder={placeholder}
                            onChange={onChangeSearch}
                            onBlur={onBlurInput}
                            value={search}
                        />
                    </div>
                    {open &&
                        <div className="immersion_admin__ti__smls__content" ref={contentRef}>
                            <div className="immersion_admin__ti__smls__content__inner">
                                {
                                    filteredOptions.length === 0
                                        ? <button>No records found!</button>
                                        : filteredOptions.map((fo, idx) =>
                                            <button key={idx + fo.value} onClick={() => onChangeSelect(fo)}>
                                                {fo.label}
                                            </button>
                                        )
                                }
                            </div>
                        </div>
                    }
                </div>
            </OutsideAlerter>
        </div>
    );
};

export const BooleanOptions = (props: IBooleanOptionsProps) => {
    const { value, onChange } = props;

    const onChangeOption = (option: '1' | '0') => {
        onChange(option);
    };

    return (
        <div className="immersion_admin__table_input_wrapper">
            <div className="immersion_admin__ti__boolean_options">
                <button
                    className={`immersion_admin__ti__options__btn ${value === '1' ? 'immersion_admin__ti__options__btn__selected' : ''}`}
                    onClick={() => onChangeOption('1')}
                >
                    <SVGIcon src={Icons.Checkmark} />
                    <span>Yes</span>
                </button>
                <button
                    className={`immersion_admin__ti__options__btn ${value === '0' ? 'immersion_admin__ti__options__btn__selected' : ''}`}
                    onClick={() => onChangeOption('0')}
                >
                    <SVGIcon src={Icons.X} />
                    <span>No</span>
                </button>
            </div>
        </div>
    );
};

export const SelectDropdown = (props: ISelectDropdownProps) => {
    const { read_only, placeholder, onChange, value, options } = props;

    const dropdownContRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);

    const [open, setOpen] = useState(false);

    const onToggleDropdown = () => {
        if (!options.length || read_only) return;
        setOpen(!open);
    };

    const onChangeSelect = (option: ISelectDropdownOption) => {
        if (read_only) return;
        setOpen(false);
        onChange(option);
    };

    return (
        <div className="immersion_admin__table_input_wrapper">
            <OutsideAlerter callback={() => setOpen(false)} isActive={open}>
                <div className="immersion_admin__ti__select_dropdown" ref={dropdownContRef}>
                    <div className="immersion_admin__ti__sd__top">
                        <button
                            className={`immersion_admin__ti__sd__btn ${read_only ? 'immersion_admin__ti__sd__btn__readonly' : ''}`}
                            onClick={onToggleDropdown}
                        >
                            {
                                value.label?.length
                                    ? <span className="color_dark">{getElipsisString(value.label, 50)}</span>
                                    : <span className="color_grey">{placeholder?.length ? placeholder : '-- Select --'}</span>
                            }
                            <div className="icons_sec">
                                <SVGIcon src={Icons.TriangleUpDownFill} color={read_only ? 'grey' : 'primary'} />
                            </div>
                        </button>
                    </div>
                    {open &&
                        <div className="immersion_admin__ti__sd__content" ref={contentRef}>
                            <div className="immersion_admin__ti__sd__content__inner">
                                {
                                    options.length === 0
                                        ? <button>No records yet!</button>
                                        : options.map((fo, idx) =>
                                            <button key={idx + fo.value} onClick={() => onChangeSelect(fo)}>
                                                {fo.label}
                                            </button>
                                        )
                                }
                            </div>
                        </div>
                    }
                </div>
            </OutsideAlerter>
        </div>
    );
};